import {
  MCP_PROTOCOL_VERSION,
  MCP_PROTOCOL_VERSION_FALLBACK,
  MCP_PROTOCOL_VERSION_PRIOR,
  type ToolDesign,
  type ToolDesignEntry
} from "@keystone/core";
import { jsonSchemaToZodSource } from "./json-schema-to-zod.js";

/**
 * Source-text templates for the emitted MCP server. Pure string transforms — no
 * runtime, fully deterministic. Two emission profiles:
 *
 *  - `designed`: hardened-ready scaffold (auth middleware, audit log hooks, Origin
 *     check, stateless Streamable HTTP). The Phase-3 hardener fills in the real
 *     auth/audit policy; this template emits the integration points.
 *  - `naive`: literal 1:1, no auth wiring, no audit log, no Origin check. This is the
 *     contrast artifact for the eval + red-team diff.
 *
 * The MCP SDK API targeted is `@modelcontextprotocol/sdk@^1.29`:
 *  - `McpServer.registerTool(name, { description, inputSchema, outputSchema, annotations }, cb)`
 *  - inputSchema/outputSchema accept a JSON Schema object directly
 *  - stdio:  `new StdioServerTransport()`
 *  - http:   `new StreamableHTTPServerTransport({ sessionIdGenerator: undefined })`
 */

export interface EmitOptions {
  kind: "designed" | "naive-baseline";
  baseUrl?: string;
}

const HEADER = `// SPDX-License-Identifier: Apache-2.0
// Generated by Keystone — do not edit by hand. Regenerate via \`keystone build\`.\n\n`;

export function renderPackageJson(design: ToolDesign): string {
  const pkg = {
    name: design.serverName,
    version: design.serverVersion,
    private: true,
    type: "module",
    main: "./dist/stdio.js",
    bin: { [design.serverName]: "./dist/stdio.js" },
    engines: { node: ">=20" },
    scripts: {
      build: "tsc -p tsconfig.json",
      start: "node ./dist/stdio.js",
      "start:http": "node ./dist/http.js"
    },
    dependencies: {
      "@modelcontextprotocol/sdk": "^1.29.0",
      express: "^4.21.0",
      helmet: "^7.1.0",
      zod: "^3.23.8"
    },
    devDependencies: {
      "@types/node": "^20.14.0",
      "@types/express": "^4.17.21",
      typescript: "^5.5.4"
    }
  };
  return JSON.stringify(pkg, null, 2) + "\n";
}

export function renderTsConfig(): string {
  return (
    JSON.stringify(
      {
        compilerOptions: {
          target: "ES2022",
          module: "NodeNext",
          moduleResolution: "NodeNext",
          strict: true,
          esModuleInterop: true,
          skipLibCheck: true,
          declaration: false,
          outDir: "./dist",
          rootDir: "./src"
        },
        include: ["src/**/*.ts"]
      },
      null,
      2
    ) + "\n"
  );
}

export function renderReadme(design: ToolDesign, opt: EmitOptions): string {
  return `# ${design.serverName}

${opt.kind === "naive-baseline" ? "**Naive 1:1 baseline server.** Generated as a contrast artifact for the Keystone eval + red-team diff. Do not deploy." : "Generated by Keystone — production MCP server scaffold."}

- MCP protocol: \`${MCP_PROTOCOL_VERSION}\` (also accepts \`${MCP_PROTOCOL_VERSION_FALLBACK}\` and \`${MCP_PROTOCOL_VERSION_PRIOR}\` for back-compat)
- Tools: ${design.tools.length}
- Transports: stdio (\`npm start\`), Streamable HTTP (\`npm run start:http\`)

## Install
\`\`\`
npm install
npm run build
npm start          # stdio
PORT=3000 npm run start:http
\`\`\`
`;
}

export function renderServer(design: ToolDesign, opt: EmitOptions): string {
  const toolBlocks = design.tools.map((t) => renderTool(t, opt)).join("\n\n");
  const designedImports =
    opt.kind === "designed"
      ? 'import { InsufficientScopeError } from "@modelcontextprotocol/sdk/server/auth/errors.js";\nimport { auditLog } from "./audit.js";\nimport { proxyFetch } from "./proxy.js";'
      : 'import { proxyFetch } from "./proxy.js";';
  return (
    HEADER +
    `import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod";
${designedImports}

// P13-H1 fix: the SDK 1.29 requires inputSchema/outputSchema as Zod *raw shape*
// (an object literal of Zod schemas) — NOT a plain JSON Schema. We convert at
// build time via @keystone/generator's jsonSchemaToZodSource. The shape below
// is what the SDK accepts at runtime registration.

export const SERVER_NAME = ${JSON.stringify(design.serverName)} as const;
export const SERVER_VERSION = ${JSON.stringify(design.serverVersion)} as const;

export function buildServer(): McpServer {
  const server = new McpServer({ name: SERVER_NAME, version: SERVER_VERSION });

${toolBlocks}

  return server;
}
`
  );
}

function renderTool(t: ToolDesignEntry, opt: EmitOptions): string {
  const annotations = JSON.stringify(t.annotations);
  const meta = {
    keystone: {
      operationIds: t.operations,
      requiredScopes: t.requiredScopes,
      pagination: t.pagination ?? null,
      kind: opt.kind
    }
  };
  // Convert JSON Schema → Zod raw-shape source so the SDK's runtime registration
  // accepts it. The emitted source is determinism-friendly: same JSON Schema in,
  // same source out (keys sorted internally). P13-H1 fix.
  const inputSchemaSrc = jsonSchemaToZodSource(t.inputSchema, { asObject: false });
  const outputSchemaSrc = jsonSchemaToZodSource(t.outputSchema, { asObject: false });

  const callBody =
    opt.kind === "designed"
      ? `      const principal = extra.authInfo?.clientId;
      const scopes = new Set(extra.authInfo?.scopes ?? []);
      const required = ${JSON.stringify(t.requiredScopes)};
      const missing = required.filter((s) => !scopes.has(s));
      if (missing.length > 0) {
        // F-03: per-tool scope enforcement; refusing here means the configured
        // requiredScopes are load-bearing, not cosmetic.
        throw new InsufficientScopeError("missing scopes: " + missing.join(", "));
      }
      auditLog({
        tool: ${JSON.stringify(t.name)},
        operationIds: ${JSON.stringify(t.operations)},
        argsDigestInput: args,
        requiredScopes: required,
        principal
      });
      const result = await proxyFetch(${JSON.stringify(t.name)}, args);
      return {
        content: [{ type: "text", text: JSON.stringify(result) }],
        structuredContent: result
      };`
      : `      const result = await proxyFetch(${JSON.stringify(t.name)}, args);
      return {
        content: [{ type: "text", text: JSON.stringify(result) }],
        structuredContent: result
      };`;

  const extraSig = opt.kind === "designed" ? ", extra: { authInfo?: { clientId?: string; scopes?: string[] } }" : "";

  return `  server.registerTool(
    ${JSON.stringify(t.name)},
    {
      title: ${JSON.stringify(t.title)},
      description: ${JSON.stringify(t.description)},
      inputSchema: ${inputSchemaSrc},
      outputSchema: ${outputSchemaSrc},
      annotations: ${annotations},
      _meta: ${JSON.stringify(meta)}
    },
    async (args: Record<string, unknown>${extraSig}) => {
${callBody}
    }
  );`;
}

export function renderProxy(opt: EmitOptions): string {
  return (
    HEADER +
    `/**
 * Upstream HTTP proxy. The generator emits a thin shim; real call routing is wired
 * by the host (URL, auth header, body shape). The default impl returns a stub
 * so \`npm test\` can exercise tool registration without hitting the network.
 */
export async function proxyFetch(toolName: string, args: Record<string, unknown>): Promise<unknown> {
  if (process.env.KEYSTONE_TEST_MODE === "1") {
    return { stub: true, tool: toolName, echoArgs: args };
  }
  const url = process.env.KEYSTONE_UPSTREAM_URL;
  if (!url) throw new Error("KEYSTONE_UPSTREAM_URL not set (and not in test mode)");
  const res = await fetch(url + "/" + toolName, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(args)
  });
  if (!res.ok) {
    // Surface a redacted error so we don't leak vendor internals back to the model.
    throw new Error("upstream " + toolName + " returned " + res.status);
  }
  return (await res.json()) as unknown;
}
${opt.kind === "naive-baseline" ? "\n// Naive baseline: no audit, no scope check, no upstream-secret guard.\n" : ""}`
  );
}

export function renderAudit(): string {
  return (
    HEADER +
    `/**
 * Audit log emitter. Emits a structured line per tool call:
 *   { ts, tool, operationIds, argsDigest, requiredScopes, principal? }
 *
 * Args are hashed (not logged in clear) so secrets that slip through schema-checks
 * never land in logs. PII redaction is delegated to the hardener; this emitter is
 * the integration point.
 *
 * Injectable sink (F-08 from Phase-2 review): in a Worker / serverless / supervised
 * container, stderr is often lost. Call \`setAuditSink(fn)\` at startup to redirect
 * lines to your platform's structured-log API (e.g. Workers Analytics Engine,
 * CloudWatch, Datadog).
 */
import { createHash } from "node:crypto";

export interface AuditEntry {
  tool: string;
  operationIds: string[];
  argsDigestInput: unknown;
  requiredScopes: string[];
  principal?: string;
}

export interface AuditLine {
  ts: string;
  tool: string;
  operationIds: string[];
  argsDigest: string;
  requiredScopes: string[];
  principal?: string;
}

export type AuditSink = (line: AuditLine) => void;

let sink: AuditSink = (line) => {
  process.stderr.write(JSON.stringify(line) + "\\n");
};

export function setAuditSink(fn: AuditSink): void {
  sink = fn;
}

export function auditLog(entry: AuditEntry): void {
  const argsDigest = createHash("sha256").update(JSON.stringify(entry.argsDigestInput ?? {})).digest("hex");
  const line: AuditLine = {
    ts: new Date().toISOString(),
    tool: entry.tool,
    operationIds: entry.operationIds,
    argsDigest,
    requiredScopes: entry.requiredScopes,
    ...(entry.principal !== undefined ? { principal: entry.principal } : {})
  };
  sink(line);
}
`
  );
}

export function renderStdio(): string {
  return (
    HEADER +
    `import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import { buildServer } from "./server.js";

const server = buildServer();
const transport = new StdioServerTransport();
await server.connect(transport);
`
  );
}

export function renderHttp(design: ToolDesign, opt: EmitOptions): string {
  const designedKind = opt.kind === "designed";
  const designedAuthImports = designedKind
    ? `
import { requireBearerAuth } from "@modelcontextprotocol/sdk/server/auth/middleware/bearerAuth.js";
import { mcpAuthMetadataRouter } from "@modelcontextprotocol/sdk/server/auth/router.js";
import { verifier, RESOURCE_URI } from "./auth.js";
import { originGuard } from "./middleware.js";`
    : "";
  const designedSetup = designedKind
    ? `
// Security headers first — helmet is cheap and blocks a long tail of trivial issues.
app.use(helmet({ contentSecurityPolicy: false, crossOriginEmbedderPolicy: false }));
app.disable("x-powered-by");

// Origin / DNS-rebinding guard BEFORE the body parser so we don't waste cycles parsing
// requests from disallowed origins (F-06 in the Phase-2 review).
app.use(originGuard());

// PRM metadata router — RFC 9728 well-known endpoint clients use to discover the AS.
app.use(
  mcpAuthMetadataRouter({
    resourceMetadataPath: "/.well-known/oauth-protected-resource",
    authorizationServerUrl: process.env.AS_URL ?? "https://example.invalid",
    resourceUri: RESOURCE_URI
  })
);

app.use(express.json({ limit: "1mb" }));

const authMiddleware = requireBearerAuth({
  verifier,
  requiredScopes: [],
  resourceMetadataUrl: "/.well-known/oauth-protected-resource"
});

app.post("/mcp", authMiddleware, scopeStepUp, handle);
// MCP Streamable HTTP also uses GET for the optional return stream / SSE channel
// (spec/2025-11-25/basic/transports: GET MAY return 405 if no server-push is offered).
app.get("/mcp", authMiddleware, scopeStepUp, handle);`
    : `
app.disable("x-powered-by");
app.use(express.json({ limit: "1mb" }));
app.post("/mcp", protocolVersionGuard, handle);
app.get("/mcp", protocolVersionGuard, handle);`;
  return (
    HEADER +
    `import express, { type Request, type Response, type NextFunction } from "express";
import helmet from "helmet";
import { StreamableHTTPServerTransport } from "@modelcontextprotocol/sdk/server/streamableHttp.js";
${designedKind ? 'import { InsufficientScopeError } from "@modelcontextprotocol/sdk/server/auth/errors.js";\n' : ""}import { buildServer } from "./server.js";${designedAuthImports}

/**
 * Accepted MCP-Protocol-Version values per ADR-0011:
 *  - "${MCP_PROTOCOL_VERSION}" (current stable, preferred)
 *  - "${MCP_PROTOCOL_VERSION_FALLBACK}" (spec back-compat default when header absent)
 *  - "${MCP_PROTOCOL_VERSION_PRIOR}" (prior rev — clients still on it during migration)
 */
const ACCEPTED_PROTOCOL_VERSIONS = ${JSON.stringify([MCP_PROTOCOL_VERSION, MCP_PROTOCOL_VERSION_FALLBACK, MCP_PROTOCOL_VERSION_PRIOR])} as const;

function protocolVersionGuard(req: Request, res: Response, next: NextFunction): void {
  const v = req.header("MCP-Protocol-Version");
  // Spec rule: omitted header is treated as the back-compat default.
  if (v && !ACCEPTED_PROTOCOL_VERSIONS.includes(v as never)) {
    res.status(400).json({ error: "unsupported MCP-Protocol-Version", accepted: ACCEPTED_PROTOCOL_VERSIONS });
    return;
  }
  next();
}

const app = express();
${designedSetup}
${
  designedKind
    ? `\n/**
 * Per-tool scope enforcement happens inside each tool callback (Phase-2 F-03) by
 * throwing InsufficientScopeError. The MCP spec rev 2025-11-25 formalises the
 * 403 + insufficient_scope step-up; this middleware maps the SDK error to the
 * spec-conformant 403 response with a WWW-Authenticate scope challenge.
 */
function scopeStepUp(req: Request, res: Response, next: NextFunction): void {
  const original = (res as Response & { _origJson?: typeof res.json }).json.bind(res);
  res.json = ((body: unknown) => {
    return original(body);
  }) as typeof res.json;
  Promise.resolve()
    .then(() => next())
    .catch((err: unknown) => {
      if (err instanceof InsufficientScopeError) {
        res
          .status(403)
          .set(
            "WWW-Authenticate",
            'Bearer error="insufficient_scope", error_description="' + err.message + '"'
          )
          .json({ error: "insufficient_scope", error_description: err.message });
        return;
      }
      throw err;
    });
}\n`
    : ""
}
async function handle(req: Request, res: Response): Promise<void> {
  // Enforce MCP-Protocol-Version on the auth path too (designed) — same accepted set.
  const v = req.header("MCP-Protocol-Version");
  if (v && !ACCEPTED_PROTOCOL_VERSIONS.includes(v as never)) {
    res.status(400).json({ error: "unsupported MCP-Protocol-Version", accepted: ACCEPTED_PROTOCOL_VERSIONS });
    return;
  }
  // Stateless mode: a fresh transport per request.
  const transport = new StreamableHTTPServerTransport({ sessionIdGenerator: undefined });
  const server = buildServer();
  res.on("close", () => {
    transport.close().catch(() => {});
    server.close().catch(() => {});
  });
  try {
    await server.connect(transport);
    await transport.handleRequest(req, res, req.body);
  } catch (err) {
    ${designedKind ? `if (err instanceof InsufficientScopeError) {
      res
        .status(403)
        .set(
          "WWW-Authenticate",
          'Bearer error="insufficient_scope", error_description="' + err.message + '"'
        )
        .json({ error: "insufficient_scope", error_description: err.message });
      return;
    }
    ` : ""}throw err;
  }
}

const port = Number(process.env.PORT ?? 3000);
app.listen(port, () => {
  process.stderr.write("listening on " + port + "\\n");
});
`
  );
}

export function renderAuth(design: ToolDesign): string {
  return (
    HEADER +
    `import type { OAuthTokenVerifier } from "@modelcontextprotocol/sdk/server/auth/provider.js";
import { InsufficientScopeError, InvalidTokenError } from "@modelcontextprotocol/sdk/server/auth/errors.js";

/**
 * Canonical RS identity (resource URI). RFC 8707 says clients MUST request a token
 * for this audience; we MUST refuse tokens whose 'aud' claim does not match.
 */
export const RESOURCE_URI = process.env.RESOURCE_URI ?? "https://example.invalid/${design.serverName}";

/**
 * BYO verifier. The MCP spec (rev ${MCP_PROTOCOL_VERSION}) REQUIRES:
 *   - Authorization: Bearer header only (never query string)
 *   - audience binding via RFC 8707 'aud' claim
 *   - NO token passthrough to upstream APIs
 *   - the 'resource' parameter on BOTH authorize and token requests (client side)
 *
 * Client identification — preferred order per the 2025-11-25 spec:
 *   1. Pre-registered client (static, AS-issued).
 *   2. Client ID Metadata Document (CIMD) — RECOMMENDED for dynamic clients.
 *      AS advertises support via 'client_id_metadata_document_supported: true' in
 *      its Authorization Server Metadata. CIMD is the modern replacement for DCR.
 *   3. RFC 7591 Dynamic Client Registration (DCR) — back-compat only.
 *
 * Replace this stub with your IdP's JWT verification (jose / openid-client / Workers
 * KV-cached JWKS). Reject:
 *   - missing or wrong 'aud' (return 401 invalid_token)
 *   - expired tokens (return 401 invalid_token)
 *   - tokens whose scopes don't satisfy the per-tool required scopes (return 403
 *     insufficient_scope step-up per spec §authorization)
 */
export const verifier: OAuthTokenVerifier = {
  async verifyAccessToken(token: string) {
    if (!token) throw new InvalidTokenError("missing token");
    // TODO: validate signature + 'aud' === RESOURCE_URI + 'exp' > now.
    // The default-deny stub keeps the server safe-by-default until wired.
    throw new InvalidTokenError("verifier not configured — wire your IdP in src/auth.ts");
  }
};

void InsufficientScopeError; // re-exported so tests can import without unused warnings
`
  );
}

export function renderMiddleware(): string {
  return (
    HEADER +
    `import type { Request, Response, NextFunction } from "express";

/**
 * Origin / DNS-rebinding guard. The MCP spec's Streamable HTTP transport requires
 * validating the Origin header on every request. The SDK's built-in check was
 * deprecated in 1.29; we own this surface.
 *
 * Hardening per Phase-2 strict review:
 *  - F-01: compare *parsed* origins, not raw string prefixes. \`http://localhost.evil.com\`
 *    no longer slips past an allowlist entry of \`http://localhost\`.
 *  - F-02: deny-by-default on missing Origin header unless \`KEYSTONE_ALLOW_NO_ORIGIN=1\`
 *    is opted in for stdio-bridge or curl tests.
 *
 * Allowlist entries are canonical origins (scheme + host + optional port). Configure
 * via \`ALLOWED_ORIGINS=csv\`; defaults include localhost dev.
 */
const DEFAULT_ALLOWED = ["http://localhost", "http://localhost:3000", "http://127.0.0.1", "http://127.0.0.1:3000"];
const PRIVATE_HOST = /^(localhost|127\\.|10\\.|192\\.168\\.|172\\.(1[6-9]|2[0-9]|3[01])\\.)/;

function canonicalOrigin(raw: string): string | null {
  try {
    const u = new URL(raw);
    // URL.origin strips trailing slash + lowercases host — exactly what we want.
    return u.origin;
  } catch {
    return null;
  }
}

export function originGuard() {
  const cfg = (process.env.ALLOWED_ORIGINS ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const allowed = new Set<string>();
  for (const a of [...DEFAULT_ALLOWED, ...cfg]) {
    const c = canonicalOrigin(a);
    if (c) allowed.add(c);
  }
  const allowNoOrigin = process.env.KEYSTONE_ALLOW_NO_ORIGIN === "1";
  return (req: Request, res: Response, next: NextFunction): void => {
    const origin = typeof req.headers.origin === "string" ? req.headers.origin : null;
    if (!origin) {
      if (!allowNoOrigin) {
        res.status(403).json({ error: "Origin header required" });
        return;
      }
      next();
      return;
    }
    const canon = canonicalOrigin(origin);
    if (!canon) {
      res.status(400).json({ error: "invalid Origin" });
      return;
    }
    if (!allowed.has(canon)) {
      res.status(403).json({ error: "Origin not allowed" });
      return;
    }
    // DNS-rebinding: if Host is in private space but Origin claims a public host,
    // suspect rebinding and refuse.
    const host = typeof req.headers.host === "string" ? req.headers.host : "";
    const originHost = (() => {
      try {
        return new URL(origin).hostname;
      } catch {
        return "";
      }
    })();
    if (host && PRIVATE_HOST.test(host) && originHost && !PRIVATE_HOST.test(originHost)) {
      res.status(403).json({ error: "DNS rebinding suspected" });
      return;
    }
    next();
  };
}
`
  );
}
